/**
 * SupportChatPanel — the integrated CHAT for one support ticket: the SupportMessage thread (operator vs
 * lab bubbles) + the unread banner + mark-read + the reply composer. The thread reads via
 * `useSupportTicket(..., live=true)` which polls on a short cadence (SUPPORT_LIVE_POLL_MS), so a lab reply
 * lands here near-real-time with no websocket — that is the "feel near-real-time" requirement.
 *
 * Read-receipts: whenever an open ticket has labo→admin unread, the operator can clear it with one
 * mark-read; the count + the cross-lab unread KPI drop on the next refetch (the mutation invalidates the
 * management root). The thread auto-scrolls to the newest message when the count grows.
 *
 * Accessibility: the thread is an aria-live polite log so AT announces an inbound reply; each bubble names
 * its author + time; the unread banner is a status. AA contrast via tokens — own/other bubbles use the
 * azure-soft / surface-2 tints (never color-as-only-signal: the author label carries the side in text).
 */
import { useEffect, useRef } from "react";
import { Badge, Button, EmptyState, ErrorState, LoadingState, useToast } from "@/ui";
import { useT } from "@/app";
import {
  SUPPORT_LIVE_POLL_MS,
  useMarkSupportRead,
  usePostSupportMessage,
  useSupportTicket,
} from "../support.api";
import { isManagementAbsent } from "../api";
import { isOwnMessage } from "../support.lib";
import { formatDateTime } from "../lib";
import type { SupportMessage } from "../support.types";
import { SupportComposer } from "./SupportComposer";
import { SupportTriagePanel } from "./SupportTriagePanel";

export interface SupportChatPanelProps {
  /** The selected ticket id, or null when nothing is selected. */
  ticketId: string | null;
  /** Gate (super-admin && online) — also gates the poll so a forbidden/offline panel never polls. */
  enabled: boolean;
}

function MessageBubble({ message, label, time }: { message: SupportMessage; label: string; time: string }) {
  const own = isOwnMessage(message.auteurType);
  return (
    <li
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: own ? "flex-end" : "flex-start",
        gap: "var(--nv-s-1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "var(--nv-s-2)",
          fontSize: "var(--nv-fs-caption)",
          color: "var(--nv-text-2, var(--nv-slate))",
        }}
      >
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span className="nv-ltr-island">{time}</span>
      </div>
      <div
        style={{
          maxWidth: "min(80%, 44ch)",
          padding: "var(--nv-s-2) var(--nv-s-3)",
          borderRadius: "var(--nv-radius-md, 10px)",
          background: own ? "var(--nv-azure-soft, var(--nv-surface-2))" : "var(--nv-surface-2)",
          color: "var(--nv-text-0)",
          border: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {message.contenu}
      </div>
    </li>
  );
}

export function SupportChatPanel({ ticketId, enabled }: SupportChatPanelProps) {
  const { t, locale } = useT();
  const toast = useToast();
  const ticket = useSupportTicket(ticketId, enabled, true);
  const post = usePostSupportMessage();
  const markRead = useMarkSupportRead();

  const messages = ticket.data?.messages ?? [];
  const unread = ticket.data?.unread ?? 0;
  const closed = ticket.data?.statut === "ferme";

  // Auto-scroll to the newest message when the thread grows (a poll brought in a reply, or we just sent).
  // Guard `scrollIntoView` — jsdom (and some embedded webviews) do not implement it.
  const endRef = useRef<HTMLDivElement | null>(null);
  const count = messages.length;
  useEffect(() => {
    if (typeof endRef.current?.scrollIntoView === "function") {
      endRef.current.scrollIntoView({ block: "end" });
    }
  }, [count]);

  if (!ticketId) {
    return (
      <EmptyState
        title={t("mgmt.support.chat.empty.title")}
        description={t("mgmt.support.chat.empty.desc")}
        glyph="✉"
      />
    );
  }

  if (ticket.isLoading) return <LoadingState label={t("mgmt.support.chat.title")} />;

  if (ticket.isError && !isManagementAbsent(ticket.error)) {
    return (
      <ErrorState
        message={ticket.error.message}
        code={ticket.error.code}
        onRetry={() => void ticket.refetch()}
      />
    );
  }

  async function onSend(contenu: string): Promise<void> {
    if (!ticketId) return;
    try {
      await post.mutateAsync({ ticketId, contenu });
      toast.success(t("mgmt.support.sent"));
      void ticket.refetch();
    } catch (e) {
      toast.error(t("mgmt.support.composer.error"));
      throw e; // keep the draft in the composer
    }
  }

  function onMarkRead(): void {
    if (!ticketId) return;
    markRead.mutate(
      { ticketId },
      {
        onSuccess: () => {
          toast.success(t("mgmt.support.read.done"));
          void ticket.refetch();
        },
      },
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)", minHeight: 0 }}>
      {/* Live indicator + unread banner */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
        <Badge tone="info" glyph="●" aria-label={`${t("mgmt.support.chat.live")} · ${SUPPORT_LIVE_POLL_MS / 1000}s`}>
          {t("mgmt.support.chat.live")}
        </Badge>
        {unread > 0 && (
          <div role="status" style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
            <Badge tone="warn" glyph="✦">
              {t("mgmt.support.chat.unread.banner", { n: unread })}
            </Badge>
            <Button size="sm" variant="secondary" onClick={onMarkRead} disabled={markRead.isPending}>
              {t("mgmt.support.chat.markread")}
            </Button>
          </div>
        )}
      </div>

      {/* Workflow controls — assignment, status transition, resolution (version-guarded off the live read) */}
      {ticket.data && <SupportTriagePanel ticket={ticket.data} enabled={enabled} />}

      {/* The thread — an aria-live log so AT announces an inbound reply */}
      <ul
        aria-live="polite"
        aria-label={t("mgmt.support.chat.title")}
        style={{
          listStyle: "none",
          margin: 0,
          padding: "var(--nv-s-2)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--nv-s-3)",
          maxHeight: "48vh",
          overflowY: "auto",
          background: "var(--nv-surface-1, var(--nv-surface))",
          borderRadius: "var(--nv-radius-md, 10px)",
        }}
      >
        {messages.length === 0 ? (
          <li style={{ textAlign: "center", color: "var(--nv-text-2, var(--nv-slate))", fontSize: "var(--nv-fs-caption)" }}>
            {t("mgmt.support.chat.thread.empty")}
          </li>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              label={t(isOwnMessage(m.auteurType) ? "mgmt.support.chat.author.admin" : "mgmt.support.chat.author.labo")}
              time={formatDateTime(m.createdAt, locale)}
            />
          ))
        )}
        <div ref={endRef} />
      </ul>

      <SupportComposer onSend={onSend} sending={post.isPending} disabled={closed} />
    </div>
  );
}
